// The sizes of a dish (ADR 0099): Quarter / Half / Full, or 1 / 2 / 3 pieces, each with
// its own price, one of them the default (the dish's own price, which is what the till
// bills until it learns about sizes).
//
// Folded until opened. The whole list is sent in one go and replaces the old one; a
// dish with sizes has at least two of them, and removing them all leaves a dish with one
// price. Prices are typed in rupees and kept as paise.

import { useState, type FormEvent, type JSX } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch } from '../../api/client.js';
import type { ItemOptionView, MenuItemRow } from '../../api/types.js';

export const SIZE_LIMITS = { min: 2, max: 8, maxLabel: 40 } as const;

interface Draft {
  readonly key: number;
  readonly label: string;
  readonly rupees: string;
}

const PRESETS: readonly { readonly name: string; readonly labels: readonly string[] }[] = [
  { name: 'Quarter / Half / Full', labels: ['Quarter', 'Half', 'Full'] },
  { name: '1 / 2 / 3 pieces', labels: ['1 piece', '2 pieces', '3 pieces'] },
];

const rupeesOf = (paise: number): string => (paise / 100).toFixed(2);

/** What is wrong with the list, in words, or null when it can be saved. */
export function checkSizes(drafts: readonly Draft[]): string | null {
  if (drafts.length < SIZE_LIMITS.min) {
    return `A dish with sizes needs at least ${SIZE_LIMITS.min} of them.`;
  }
  const seen = new Set<string>();
  for (const d of drafts) {
    const label = d.label.trim();
    if (label === '') return 'Every size needs a name.';
    if (label.length > SIZE_LIMITS.maxLabel) {
      return `A size name can be up to ${SIZE_LIMITS.maxLabel} characters.`;
    }
    const price = Number(d.rupees);
    if (d.rupees.trim() === '' || !Number.isFinite(price) || price < 0) {
      return `Give “${label}” a price of zero or more.`;
    }
    const norm = label.toLowerCase().replace(/\s+/g, ' ');
    if (seen.has(norm)) return `Two sizes are called “${label}”.`;
    seen.add(norm);
  }
  return null;
}

export function ItemSizesEditor(props: {
  readonly orgId: string;
  readonly menuId: string;
  readonly detailKey: readonly unknown[];
  readonly item: MenuItemRow;
  readonly itemName: string;
}): JSX.Element {
  const { orgId, menuId, detailKey, item, itemName } = props;
  const qc = useQueryClient();
  const current: readonly ItemOptionView[] = item.options ?? [];

  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState<readonly Draft[]>([]);
  const [defaultKey, setDefaultKey] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);
  const [nextKey, setNextKey] = useState(1);

  const save = useMutation({
    mutationFn: (body: {
      options: readonly { label: string; pricePaise: number; isDefault: boolean }[];
    }) =>
      apiFetch<unknown>(`/organizations/${orgId}/menus/${menuId}/items/${item.id}/options`, {
        method: 'PUT',
        body: { ...body, expectedVersion: item.version },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: detailKey });
      setOpen(false);
    },
  });

  function begin(): void {
    if (current.length > 0) {
      const ds = current.map((o, i) => ({
        key: i + 1,
        label: o.label,
        rupees: rupeesOf(o.pricePaise),
      }));
      setDrafts(ds);
      setDefaultKey(
        ds[
          Math.max(
            0,
            current.findIndex((o) => o.isDefault),
          )
        ]!.key,
      );
      setNextKey(ds.length + 1);
    } else {
      setDrafts([]);
      setDefaultKey(0);
      setNextKey(1);
    }
    setProblem(null);
    save.reset();
    setOpen(true);
  }

  function usePreset(labels: readonly string[]): void {
    const ds = labels.map((label, i) => ({
      key: nextKey + i,
      label,
      rupees: drafts[i]?.rupees ?? '',
    }));
    setDrafts(ds);
    setDefaultKey(ds[ds.length - 1]!.key);
    setNextKey(nextKey + ds.length);
    setProblem(null);
  }

  function add(): void {
    if (drafts.length >= SIZE_LIMITS.max) return;
    const d = { key: nextKey, label: '', rupees: '' };
    setDrafts([...drafts, d]);
    if (drafts.length === 0) setDefaultKey(d.key);
    setNextKey(nextKey + 1);
  }

  function change(key: number, patch: Partial<Draft>): void {
    setDrafts(drafts.map((d) => (d.key === key ? { ...d, ...patch } : d)));
    setProblem(null);
  }

  function remove(key: number): void {
    const rest = drafts.filter((d) => d.key !== key);
    setDrafts(rest);
    if (key === defaultKey && rest.length > 0) setDefaultKey(rest[rest.length - 1]!.key);
    setProblem(null);
  }

  function onSave(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const wrong = checkSizes(drafts);
    if (wrong) {
      setProblem(wrong);
      return;
    }
    save.mutate({
      options: drafts.map((d) => ({
        label: d.label.trim(),
        pricePaise: Math.round(Number(d.rupees) * 100),
        isDefault: d.key === defaultKey,
      })),
    });
  }

  function onRemoveAll(): void {
    save.mutate({ options: [] });
  }

  return (
    <div className="sizes-editor">
      <button
        type="button"
        className="button secondary"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : begin())}
      >
        {open ? 'Close sizes' : current.length > 0 ? 'Change sizes' : 'Add sizes'}
      </button>

      {open ? (
        <form className="sizes-panel" onSubmit={onSave} aria-label={`sizes of ${itemName}`}>
          {drafts.length === 0 ? (
            <div className="sizes-presets">
              <span className="muted">Start from</span>
              {PRESETS.map((p) => (
                <button type="button" key={p.name} onClick={() => usePreset(p.labels)}>
                  {p.name}
                </button>
              ))}
            </div>
          ) : (
            <ul className="sizes-list">
              {drafts.map((d, i) => (
                <li key={d.key} className="sizes-row">
                  <input
                    aria-label={`size ${i + 1} name`}
                    placeholder="Half"
                    maxLength={SIZE_LIMITS.maxLabel}
                    value={d.label}
                    onChange={(e) => change(d.key, { label: e.target.value })}
                  />
                  <input
                    aria-label={`size ${i + 1} price`}
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="₹"
                    value={d.rupees}
                    onChange={(e) => change(d.key, { rupees: e.target.value })}
                  />
                  <label className="radio">
                    <input
                      type="radio"
                      name={`default-size-${item.id}`}
                      aria-label={`size ${i + 1} is the default`}
                      checked={d.key === defaultKey}
                      onChange={() => setDefaultKey(d.key)}
                    />
                    <span>default</span>
                  </label>
                  <button
                    type="button"
                    aria-label={`remove size ${i + 1}`}
                    onClick={() => remove(d.key)}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}

          <p className="muted">
            The default size is the dish’s own price: the one the menu shows first, and the one the
            till bills for now.
          </p>
          {problem ? (
            <p className="error" role="alert">
              {problem}
            </p>
          ) : null}
          {save.error ? (
            <p className="error" role="alert">
              {save.error instanceof ApiError ? save.error.message : 'could not save the sizes'}
            </p>
          ) : null}

          <div className="row">
            {drafts.length > 0 ? (
              <button type="button" onClick={add} disabled={drafts.length >= SIZE_LIMITS.max}>
                Add a size
              </button>
            ) : null}
            {drafts.length > 0 ? (
              <button type="submit" disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save sizes'}
              </button>
            ) : null}
            {current.length > 0 ? (
              <button type="button" onClick={onRemoveAll} disabled={save.isPending}>
                Remove all sizes
              </button>
            ) : null}
          </div>
        </form>
      ) : null}
    </div>
  );
}
